import { differenceInCalendarDays, format, isToday, isTomorrow } from 'date-fns';

import { type PlanPricing, type SubscriptionBillingCycle, type SubscriptionPlan } from '@air/types';
import { parseAirDateToISO } from '@air/utilities';
import { reportErrorToBugsnag } from '@air/utils-error';

import { type EnhancedSubscription } from '~/providers/SubscriptionProvider';

type SubscriptionPlanDisplayName = 'Free' | 'Creator' | 'Starter' | 'Plus' | 'Pro' | 'Business' | 'Enterprise' | 'Demo';

export const DEFAULT_TRIAL_DURATION_DAYS = 7;

export const subscriptionPlanMap: { [key in SubscriptionPlan]: SubscriptionPlanDisplayName } = {
  basic: 'Free',
  creator: 'Creator',
  starter: 'Starter',
  plus: 'Plus',
  pro: 'Pro',
  business: 'Business',
  enterprise: 'Enterprise',
  demo: 'Demo',
};

export const isPlanEligibleForTrial = (plan: Pick<PlanPricing, 'pricingModel'>): boolean => {
  return plan.pricingModel !== 'credit';
};

export const getTrialDurationDays = (purchaseDate: string, trialEndDate: string): number => {
  const start = new Date(parseAirDateToISO(purchaseDate));
  const end = new Date(parseAirDateToISO(trialEndDate));

  if (Number.isNaN(start.getTime()) || Number.isNaN(end.getTime())) {
    return DEFAULT_TRIAL_DURATION_DAYS;
  }

  const days = differenceInCalendarDays(end, start);

  return days > 0 ? days : DEFAULT_TRIAL_DURATION_DAYS;
};

/**
 * Checks if a subscription is currently on a trial.
 * @param subscription - The subscription object to check
 * @returns True if the subscription has a trial end date in the future
 */
export const isSubscriptionOnTrial = (subscription: { trialEndDate?: string | null } | undefined): boolean => {
  if (!subscription?.trialEndDate) {
    return false;
  }

  const trialEnd = new Date(parseAirDateToISO(subscription.trialEndDate));

  if (Number.isNaN(trialEnd.getTime())) {
    reportErrorToBugsnag({
      error: new Error('Invalid trial end date'),
      context: 'isSubscriptionOnTrial',
      metadata: { trialEndDate: subscription.trialEndDate },
    });
    return false;
  }

  return trialEnd > new Date();
};

/**
 * Formats a trial end date from Air's backend format to a human-readable string.
 * Shows "today" or "tomorrow" for dates within the next 2 days, otherwise shows full date.
 * @param trialEndDate - The trial end date string from the backend (Postgres timestamp format)
 * @returns Formatted date string like "today", "tomorrow", "November 20, 2025", or empty string if invalid
 */
export const formatTrialEndDate = (trialEndDate: string): string => {
  const date = new Date(parseAirDateToISO(trialEndDate));

  if (Number.isNaN(date.getTime())) {
    reportErrorToBugsnag({
      error: new Error('Invalid trial end date'),
      context: 'formatTrialEndDate',
      metadata: { trialEndDate },
    });
    return '';
  }

  if (isToday(date)) {
    return 'today';
  }
  if (isTomorrow(date)) {
    return 'tomorrow';
  }

  return format(date, 'MMMM d, yyyy');
};

type StorageUpgradeParams = {
  subscription?: EnhancedSubscription;
  storage?: { isOutOfStorage?: boolean; isAlmostOutOfStorage?: boolean };
};

/**
 * Determines if the storage upgrade CTA should be shown.
 * Shows for free plan users or pro plan users who are out of or almost out of storage.
 * @param subscription - Subscription information
 * @param storage - Storage information
 * @returns True if storage upgrade CTA should be shown
 */
export const shouldShowStorageUpgrade = ({ subscription, storage }: StorageUpgradeParams): boolean => {
  if (subscription?.isCreditModel) {
    return false;
  }

  if (subscription?.isInFreePlan) {
    return true;
  }

  if (subscription?.isOnProPlan) {
    return storage?.isOutOfStorage || storage?.isAlmostOutOfStorage || false;
  }

  return false;
};

export const isBillingCycle = (value: string): value is SubscriptionBillingCycle => {
  const BILLING_CYCLES = ['monthly', 'annual', 'quarter', 'semiAnnual'] satisfies SubscriptionBillingCycle[];

  return BILLING_CYCLES.some((cycle) => cycle === value);
};
